import type { PathDef, Source } from './types';

const GJERDINGEN: Source = { label: 'Robert O. Gjerdingen, Music in the Galant Style (Oxford, 2007)' };
const RIEPEL: Source = { label: 'Joseph Riepel, Anfangsgründe zur musicalischen Setzkunst (1752–1755)' };
const KOCH: Source = { label: 'Heinrich Christoph Koch, Versuch einer Anleitung zur Composition (1782–1793)' };
const CAPLIN: Source = { label: 'William E. Caplin, Classical Form (Oxford, 1998)' };
const FENAROLI: Source = { label: 'Fedele Fenaroli, Regole musicali per i principianti di cembalo (Naples, 1775)' };
const SANGUINETTI: Source = { label: 'Giorgio Sanguinetti, The Art of Partimento (Oxford, 2012)' };
const OMT: Source = { label: 'Open Music Theory: Galant schemata', url: 'https://openmusictheory.github.io/schemataOpensAndCloses' };

const MAJOR_VARIANTS: Array<[number, number, string]> = [
  [-3, -5, 'G major'],
  [3, 5, 'F major'],
  [1, 2, 'D major'],
];

export const PATHS: PathDef[] = [
  {
    id: 'authentic-cadence',
    name: 'Authentic cadence',
    feeling: 'Arriving home',
    family: 'cadence',
    listenFor: 'The bass leaps from the dominant down to the tonic while the leading tone rises into the tonic above it.',
    plain: 'The chord that pulls hardest toward home moves to home itself, the bass leaping from the fifth note of the key to the keynote. The pull lets go.',
    theory:
      'The strongest punctuation in tonal music: a root-position dominant (V or V⁷) moves to a root-position tonic. When the melody also lands on the tonic note it is a perfect authentic cadence (PAC); on the third or fifth it is imperfect (IAC).',
    bassFormula: '1 – 4 – 5 – 1',
    melodyFormula: '3 – 2 – 2 – 1',
    harmonyFormula: 'I – ii⁶ – V⁷ – I',
    history:
      'The cadence grew out of the Renaissance clausula. Eighteenth-century theorists treated it as the music’s full stop; Koch built his account of form on these resting points.',
    key: 'C',
    prototype: [
      { rn: 'I', dur: 2, notes: ['C3', 'C4', 'G4', 'E5'] },
      { rn: 'ii6', dur: 2, notes: ['F3', 'A3', 'F4', 'D5'] },
      { rn: 'V7', dur: 2, notes: ['G2', 'B3', 'F4', 'D5'] },
      { rn: 'I', dur: 4, notes: ['C3', 'C4', 'E4', 'C5'] },
    ],
    whatIf: {
      label: 'Stop on the third',
      question: 'What if the melody stopped on E instead of C?',
      effect: 'The harmony still arrives, but the sentence sounds less final: an imperfect authentic cadence.',
      prototype: [
        { rn: 'I', dur: 2, notes: ['C3', 'C4', 'G4', 'E5'] },
        { rn: 'ii6', dur: 2, notes: ['F3', 'A3', 'F4', 'D5'] },
        { rn: 'V7', dur: 2, notes: ['G2', 'B3', 'F4', 'D5'] },
        { rn: 'I', dur: 4, notes: ['C3', 'C4', 'E4', 'E5'] },
      ],
    },
    heardIn: ['bwv846:home', 'bwv846:tilt', 'bwv846:settling'],
    sources: [KOCH, CAPLIN],
    variants: MAJOR_VARIANTS,
  },
  {
    id: 'half-cadence',
    name: 'Half cadence',
    feeling: 'A comma, a question',
    family: 'cadence',
    listenFor: 'The phrase stops on the dominant chord and leaves you waiting for the rest of the sentence.',
    plain: 'The phrase stops on the chord that pulls hardest toward home, and leaves you waiting for the rest of the sentence.',
    theory:
      'A phrase that ends on the dominant instead of the tonic. It punctuates without closing, so the next phrase is heard as an answer. The pairing of a half cadence and a later authentic cadence is the classical period.',
    bassFormula: '1 – 6 – 4 – 5',
    harmonyFormula: 'I – vi – ii⁶ – V',
    history: 'Koch counted phrase endings among the “resting points of the mind”; this one articulates without closing. Caplin places it at the end of antecedent phrases.',
    key: 'C',
    prototype: [
      { rn: 'I', dur: 2, notes: ['C3', 'C4', 'G4', 'E5'] },
      { rn: 'vi', dur: 2, notes: ['A2', 'C4', 'A4', 'E5'] },
      { rn: 'ii6', dur: 2, notes: ['F3', 'D4', 'A4', 'F5'] },
      { rn: 'V', dur: 4, notes: ['G3', 'B3', 'G4', 'D5'] },
    ],
    whatIf: {
      label: 'Finish the sentence',
      question: 'What if the phrase went on to the tonic?',
      effect: 'Adding V⁷–I turns the question into a statement.',
      prototype: [
        { rn: 'I', dur: 2, notes: ['C3', 'C4', 'G4', 'E5'] },
        { rn: 'vi', dur: 2, notes: ['A2', 'C4', 'A4', 'E5'] },
        { rn: 'ii6', dur: 2, notes: ['F3', 'D4', 'A4', 'F5'] },
        { rn: 'V7', dur: 2, notes: ['G3', 'B3', 'F4', 'D5'] },
        { rn: 'I', dur: 4, notes: ['C3', 'C4', 'E4', 'C5'] },
      ],
    },
    heardIn: ['bwv846:tilt'],
    sources: [KOCH, CAPLIN],
    variants: MAJOR_VARIANTS,
  },
  {
    id: 'deceptive-cadence',
    name: 'Deceptive cadence',
    feeling: 'The ground gives way',
    family: 'cadence',
    listenFor: 'Everything points home, the melody even arrives, but the bass steps up to A instead of falling to C.',
    plain: 'Everything points home and the melody may even arrive, but the bass steps up one note instead of falling home, and a different chord takes home’s place.',
    theory:
      'The dominant resolves to vi instead of I. The upper voices behave exactly as in an authentic cadence; only the bass moves up a step. Composers use it to extend a phrase and make the real arrival later feel stronger.',
    bassFormula: '1 – 4 – 5 – 6',
    harmonyFormula: 'I – ii⁶ – V⁷ – vi',
    history: 'Italian theorists called it cadenza d’inganno, the cadence of deceit. In the classical style it is a standard way to postpone closure.',
    key: 'C',
    prototype: [
      { rn: 'I', dur: 2, notes: ['C3', 'C4', 'G4', 'E5'] },
      { rn: 'ii6', dur: 2, notes: ['F3', 'A3', 'F4', 'D5'] },
      { rn: 'V7', dur: 2, notes: ['G2', 'B3', 'F4', 'D5'] },
      { rn: 'vi', dur: 4, notes: ['A2', 'C4', 'E4', 'C5'] },
    ],
    whatIf: {
      label: 'Go home',
      question: 'What if the bass fell to C?',
      effect: 'The same upper voices over C give the authentic cadence. Only one note differs.',
      prototype: [
        { rn: 'I', dur: 2, notes: ['C3', 'C4', 'G4', 'E5'] },
        { rn: 'ii6', dur: 2, notes: ['F3', 'A3', 'F4', 'D5'] },
        { rn: 'V7', dur: 2, notes: ['G2', 'B3', 'F4', 'D5'] },
        { rn: 'I', dur: 4, notes: ['C3', 'C4', 'E4', 'C5'] },
      ],
    },
    heardIn: ['bwv846:home'],
    sources: [CAPLIN],
    variants: MAJOR_VARIANTS,
  },
  {
    id: 'home-orbit',
    name: 'Tonic prolongation',
    feeling: 'Leaving home without leaving',
    family: 'prolongation',
    listenFor: 'The bass barely moves, a step down and back, while the chords above lean away and return.',
    theory:
      'Neighbouring chords decorate the tonic without replacing it: I – ii⁴₂ – V⁶₅ – I over a bass that moves 1 – 1 – 7 – 1. The key is stated, and gravity is felt, before the piece goes anywhere.',
    bassFormula: '1 – 1 – 7 – 1',
    melodyFormula: '3 – 4 – 4 – 3',
    harmonyFormula: 'I – ii⁴₂ – V⁶₅ – I',
    history: 'A universal opening move of tonal music. Bach opens the Well-Tempered Clavier with it.',
    key: 'C',
    prototype: [
      { rn: 'I', dur: 2, notes: ['C3', 'E4', 'G4', 'C5', 'E5'] },
      { rn: 'ii42', dur: 2, notes: ['C3', 'D4', 'A4', 'D5', 'F5'] },
      { rn: 'V65', dur: 2, notes: ['B2', 'D4', 'G4', 'D5', 'F5'] },
      { rn: 'I', dur: 4, notes: ['C3', 'E4', 'G4', 'C5', 'E5'] },
    ],
    whatIf: {
      label: 'Stand still',
      question: 'What if the harmony never moved?',
      effect: 'Four bars of the same chord: stable, but nothing pulls and nothing arrives.',
      prototype: [
        { rn: 'I', dur: 2, notes: ['C3', 'E4', 'G4', 'C5', 'E5'] },
        { rn: 'I', dur: 2, notes: ['C3', 'E4', 'G4', 'C5', 'E5'] },
        { rn: 'I', dur: 2, notes: ['C3', 'E4', 'G4', 'C5', 'E5'] },
        { rn: 'I', dur: 4, notes: ['C3', 'E4', 'G4', 'C5', 'E5'] },
      ],
    },
    heardIn: ['bwv846:orbit'],
    sources: [CAPLIN],
    variants: MAJOR_VARIANTS,
  },
  {
    id: 'pivot-modulation',
    name: 'Pivot to the dominant',
    feeling: 'The floor tilts',
    family: 'colour',
    listenFor: 'The chord in the middle belongs to both keys; the F♯ that follows decides which one wins.',
    theory:
      'A modulation through a chord common to both keys. A minor is vi in C and ii in G; after it, D⁷ (the dominant of G) and G itself establish the new tonic. The dominant is the most common first destination in major-key music.',
    bassFormula: 'C: 1 – 6 · G: 5 – 1',
    harmonyFormula: 'C: I – vi = G: ii – V⁷ – I',
    history: 'Standard in the eighteenth century: the move to the dominant organizes both Baroque binary forms and the classical sonata exposition.',
    key: 'C',
    prototype: [
      { rn: 'I', dur: 2, notes: ['C3', 'G3', 'E4', 'C5'] },
      { rn: 'vi', dur: 2, notes: ['A2', 'A3', 'E4', 'C5'] },
      { rn: 'V7', key: 'G', dur: 2, notes: ['D3', 'A3', 'F#4', 'C5'] },
      { rn: 'I', key: 'G', dur: 4, notes: ['G2', 'B3', 'G4', 'B4'] },
    ],
    whatIf: {
      label: 'Remove the F♯',
      question: 'What if the F♯ were F?',
      effect: 'D minor instead of D major: the music stays in C and merely pauses on G, a half cadence.',
      prototype: [
        { rn: 'I', dur: 2, notes: ['C3', 'G3', 'E4', 'C5'] },
        { rn: 'vi', dur: 2, notes: ['A2', 'A3', 'E4', 'C5'] },
        { rn: 'ii7', dur: 2, notes: ['D3', 'A3', 'F4', 'C5'] },
        { rn: 'V', dur: 4, notes: ['G2', 'B3', 'G4', 'B4'] },
      ],
    },
    heardIn: ['bwv846:tilt'],
    sources: [CAPLIN],
    variants: [[3, 5, 'F major'], [1, 2, 'D major']],
  },
  {
    id: 'tonicization',
    name: 'Tonicization',
    feeling: 'A room changes its light',
    family: 'colour',
    listenFor: 'One sharpened note (C♯) makes D minor sound briefly like a home, before C major takes over again.',
    theory:
      'An applied dominant (here V⁶₅ of ii) treats a chord of the key as a temporary tonic. Unlike a modulation, the visit is short and the home key is never in doubt.',
    bassFormula: '1 – ♯1 – 2 – 5 – 1',
    harmonyFormula: 'I – V⁶₅/ii – ii – V⁷ – I',
    history: 'The name “applied” or “secondary” dominant came later; the practice is everywhere in Bach and Mozart.',
    key: 'C',
    prototype: [
      { rn: 'I', dur: 2, notes: ['C3', 'G3', 'E4', 'C5'] },
      { rn: 'V65/ii', dur: 2, notes: ['C#3', 'G3', 'E4', 'A4'] },
      { rn: 'ii', dur: 2, notes: ['D3', 'F3', 'D4', 'A4'] },
      { rn: 'V7', dur: 2, notes: ['G2', 'F3', 'D4', 'B4'] },
      { rn: 'I', dur: 4, notes: ['C3', 'E3', 'C4', 'C5'] },
    ],
    whatIf: {
      label: 'Remove the C♯',
      question: 'What if the C♯ stayed C?',
      effect: 'The second chord becomes A minor seventh: the same motion, but no sudden change of light.',
      prototype: [
        { rn: 'I', dur: 2, notes: ['C3', 'G3', 'E4', 'C5'] },
        { rn: 'vi65', dur: 2, notes: ['C3', 'G3', 'E4', 'A4'] },
        { rn: 'ii', dur: 2, notes: ['D3', 'F3', 'D4', 'A4'] },
        { rn: 'V7', dur: 2, notes: ['G2', 'F3', 'D4', 'B4'] },
        { rn: 'I', dur: 4, notes: ['C3', 'E3', 'C4', 'C5'] },
      ],
    },
    heardIn: ['bwv846:shadow'],
    sources: [CAPLIN],
    variants: MAJOR_VARIANTS,
  },
  {
    id: 'romanesca',
    name: 'Romanesca',
    feeling: 'A stately beginning',
    family: 'opening',
    listenFor: 'A bass that walks down step by step and then drops, under a melody that keeps returning to the same note.',
    theory:
      'An opening schema. In its galant form the bass descends 1 – 7 – 6 – 3 while the melody stresses scale degrees 1 and 5. It usually opens a phrase and is often answered by a Prinner.',
    bassFormula: '1 – 7 – 6 – 3',
    melodyFormula: '1 – 5 – 1 – 1',
    harmonyFormula: 'I – V⁶ – vi – I⁶',
    history: 'Named after a sixteenth-century formula for singing poetry. Gjerdingen describes three eighteenth-century variants: leaping, stepwise and a galant hybrid.',
    key: 'C',
    prototype: [
      { rn: 'I', dur: 2, notes: ['C3', 'E4', 'C5'] },
      { rn: 'V6', dur: 2, notes: ['B2', 'D4', 'G4'] },
      { rn: 'vi', dur: 2, notes: ['A2', 'E4', 'C5'] },
      { rn: 'I6', dur: 4, notes: ['E2', 'G4', 'C5'] },
    ],
    heardIn: [],
    sources: [GJERDINGEN, OMT],
    variants: MAJOR_VARIANTS,
  },
  {
    id: 'prinner',
    name: 'Prinner',
    feeling: 'A gentle answer',
    family: 'answer',
    listenFor: 'Melody and bass step down together, four steps each, ten notes apart.',
    theory:
      'A four-stage descent: bass 4 – 3 – 2 – 1 under melody 6 – 5 – 4 – 3, in parallel tenths. It typically answers an opening schema, and moved up a fifth it can carry the music into the dominant key.',
    bassFormula: '4 – 3 – 2 – 1',
    melodyFormula: '6 – 5 – 4 – 3',
    harmonyFormula: 'IV – I⁶ – vii°⁶ – I',
    history: 'Named by Gjerdingen after the seventeenth-century theorist Johann Jacob Prinner. It is one of the most frequent schemata of galant music.',
    key: 'C',
    prototype: [
      { rn: 'IV', dur: 2, notes: ['F3', 'C4', 'A4'] },
      { rn: 'I6', dur: 2, notes: ['E3', 'C4', 'G4'] },
      { rn: 'viio6', dur: 2, notes: ['D3', 'B3', 'F4'] },
      { rn: 'I', dur: 4, notes: ['C3', 'C4', 'E4'] },
    ],
    heardIn: [],
    sources: [GJERDINGEN, OMT],
    variants: MAJOR_VARIANTS,
  },
  {
    id: 'fonte',
    name: 'Fonte',
    feeling: 'Down two steps, from shadow into light',
    family: 'sequence',
    listenFor: 'The same short move is played twice, the second time one step lower: first in minor, then in major.',
    theory:
      'A descending sequence of two units: a dominant resolving to ii, then a dominant resolving to I. Bass ♯1 – 2, 7 – 1. It typically opens the second half of a galant minuet.',
    bassFormula: '♯1 – 2 | 7 – 1',
    melodyFormula: '5 – 4 | 4 – 3',
    harmonyFormula: 'V⁶₅/ii – ii | V⁶₅ – I',
    history: 'One of three patterns (Monte, Fonte, Ponte) named by Joseph Riepel in the 1750s; “fonte” means fountain, flowing downward.',
    key: 'C',
    prototype: [
      { rn: 'V65/ii', dur: 2, notes: ['C#3', 'E4', 'G4'] },
      { rn: 'ii', dur: 2, notes: ['D3', 'D4', 'F4'] },
      { rn: 'V65', dur: 2, notes: ['B2', 'D4', 'F4'] },
      { rn: 'I', dur: 4, notes: ['C3', 'C4', 'E4'] },
    ],
    heardIn: [],
    sources: [RIEPEL, GJERDINGEN],
    variants: MAJOR_VARIANTS,
  },
  {
    id: 'monte',
    name: 'Monte',
    feeling: 'Climbing, one step at a time',
    family: 'sequence',
    listenFor: 'The same short move twice, the second time one step higher. Each arrival sounds brighter.',
    theory:
      'An ascending sequence: a dominant resolving to IV, then a dominant resolving to V. Bass 3 – 4, ♯4 – 5. It builds energy, often in the second half of a binary form.',
    bassFormula: '3 – 4 | ♯4 – 5',
    harmonyFormula: 'V⁶₅/IV – IV | V⁶₅/V – V',
    history: 'Riepel’s “mountain”: the climbing counterpart of the Fonte.',
    key: 'C',
    prototype: [
      { rn: 'V65/IV', dur: 2, notes: ['E3', 'C4', 'Bb4'] },
      { rn: 'IV', dur: 2, notes: ['F3', 'C4', 'A4'] },
      { rn: 'V65/V', dur: 2, notes: ['F#3', 'C4', 'A4'] },
      { rn: 'V', dur: 4, notes: ['G3', 'B3', 'G4'] },
    ],
    heardIn: [],
    sources: [RIEPEL, GJERDINGEN],
    variants: MAJOR_VARIANTS,
  },
  {
    id: 'ponte',
    name: 'Ponte',
    feeling: 'Waiting on the doorstep',
    family: 'pedal',
    listenFor: 'The bass stays on the dominant note while the harmony above keeps shifting. The longer it lasts, the stronger the pull home.',
    plain: 'The bass holds one note, the one that pulls hardest toward home, while the chords above keep shifting. The longer it lasts, the stronger the pull.',
    theory:
      'A held dominant, often decorated with V⁷ and the cadential ⁶₄, that bridges into a return. In classical forms the same device is called standing on the dominant.',
    bassFormula: '5 – 5 – 5 – 5 → 1',
    harmonyFormula: 'V – V⁷ – I⁶₄ – V⁷ → I',
    history: 'Riepel’s “bridge”. In sonata forms it prepares the recapitulation; in Bach’s C major prelude it lasts eight bars.',
    key: 'C',
    prototype: [
      { rn: 'V', dur: 2, notes: ['G2', 'D4', 'B4'] },
      { rn: 'V7', dur: 2, notes: ['G2', 'F4', 'B4'] },
      { rn: 'Cad64', dur: 2, notes: ['G2', 'E4', 'C5'] },
      { rn: 'V7', dur: 2, notes: ['G2', 'F4', 'B4'] },
      { rn: 'I', dur: 4, notes: ['C3', 'E4', 'C5'] },
    ],
    whatIf: {
      label: 'No waiting',
      question: 'What if the dominant resolved at once?',
      effect: 'The arrival comes early and weighs less. Duration on the dominant is what builds expectation.',
      prototype: [
        { rn: 'V', dur: 2, notes: ['G2', 'D4', 'B4'] },
        { rn: 'I', dur: 4, notes: ['C3', 'E4', 'C5'] },
      ],
    },
    heardIn: ['bwv846:waiting'],
    sources: [RIEPEL, GJERDINGEN, CAPLIN],
    variants: MAJOR_VARIANTS,
  },
  {
    id: 'quiescenza',
    name: 'Quiescenza',
    feeling: 'Settling after arrival',
    family: 'pedal',
    listenFor: 'Over a held tonic bass, one voice sinks a step, sinks again, then rises back home.',
    theory:
      'A closing schema over a tonic pedal: an inner or upper voice moves ♭7 – 6 – ♮7 – 1. It confirms an arrival that has already happened, like a sigh after the last word.',
    bassFormula: '1 – 1 – 1 – 1',
    melodyFormula: '♭7 – 6 – 7 – 1',
    harmonyFormula: 'V⁷/IV – IV⁶₄ – vii° – I (over the tonic)',
    history: 'Named by Gjerdingen from the Italian for “quietness”. The last bars of Bach’s C major prelude trace it.',
    key: 'C',
    prototype: [
      { rn: 'V7/IV', dur: 2, notes: ['C3', 'E4', 'Bb4'] },
      { rn: 'IV64', dur: 2, notes: ['C3', 'F4', 'A4'] },
      { rn: 'viio', dur: 2, notes: ['C3', 'F4', 'B4'] },
      { rn: 'I', dur: 4, notes: ['C3', 'E4', 'C5'] },
    ],
    whatIf: {
      label: 'Without the flat seventh',
      question: 'What if B♭ were C?',
      effect: 'The gesture loses its first sinking step, and with it most of its warmth.',
      prototype: [
        { rn: 'I', dur: 2, notes: ['C3', 'E4', 'C5'] },
        { rn: 'IV64', dur: 2, notes: ['C3', 'F4', 'A4'] },
        { rn: 'viio', dur: 2, notes: ['C3', 'F4', 'B4'] },
        { rn: 'I', dur: 4, notes: ['C3', 'E4', 'C5'] },
      ],
    },
    heardIn: ['bwv846:settling'],
    sources: [GJERDINGEN],
    variants: MAJOR_VARIANTS,
  },
  {
    id: 'converging',
    name: 'Converging on the dominant',
    feeling: 'Everything funnels in',
    family: 'cadence',
    listenFor: 'The bass creeps up by a half step while the voices above step down, squeezing toward one chord.',
    theory:
      'Bass 4 – ♯4 – 5 under a descending upper line. The chromatic ♯4 acts as a leading tone to the dominant, so the phrase closes on V with extra force. Bach’s prelude adds ♭6 above for a two-sided squeeze.',
    bassFormula: '4 – ♯4 – 5',
    harmonyFormula: 'IV – vii°/V – V',
    history: 'Gjerdingen lists a converging cadence among galant half-cadence formulas.',
    key: 'C',
    prototype: [
      { rn: 'I', dur: 2, notes: ['C3', 'G4', 'C5'] },
      { rn: 'IV', dur: 2, notes: ['F3', 'A4', 'C5'] },
      { rn: 'viio/V', dur: 2, notes: ['F#3', 'A4', 'C5'] },
      { rn: 'V', dur: 4, notes: ['G3', 'G4', 'B4'] },
    ],
    whatIf: {
      label: 'Without the ♯4',
      question: 'What if the bass stayed on F?',
      effect: 'The same destination, but the squeeze is gone.',
      prototype: [
        { rn: 'I', dur: 2, notes: ['C3', 'G4', 'C5'] },
        { rn: 'IV', dur: 2, notes: ['F3', 'A4', 'C5'] },
        { rn: 'IV', dur: 2, notes: ['F3', 'A4', 'C5'] },
        { rn: 'V', dur: 4, notes: ['G3', 'G4', 'B4'] },
      ],
    },
    heardIn: ['bwv846:funnel'],
    sources: [GJERDINGEN],
    variants: MAJOR_VARIANTS,
  },
  {
    id: 'fifths-sequence',
    name: 'Falling-fifths sequence',
    feeling: 'Falling dominoes',
    family: 'sequence',
    listenFor: 'Each chord seems to push over the next one: the roots fall by fifths through every step of the key.',
    plain: 'Each chord seems to push over the next, like a row of dominoes falling downhill through the key.',
    theory:
      'Roots descend by fifth (or rise by fourth) through the diatonic chords: I – IV – vii° – iii – vi – ii – V – I. Each move repeats the pull of dominant to tonic, so the whole chain feels driven.',
    bassFormula: '1 – 4 – 7 – 3 – 6 – 2 – 5 – 1',
    harmonyFormula: 'I – IV – vii° – iii – vi – ii – V – I',
    history: 'The motor of Vivaldi’s and Corelli’s episodes and of countless baroque sequences.',
    key: 'C',
    prototype: [
      { rn: 'I', dur: 1.5, notes: ['C3', 'E4', 'G4', 'C5'] },
      { rn: 'IV', dur: 1.5, notes: ['F3', 'F4', 'A4', 'C5'] },
      { rn: 'viio', dur: 1.5, notes: ['B2', 'F4', 'B4', 'D5'] },
      { rn: 'iii', dur: 1.5, notes: ['E3', 'G4', 'B4', 'E5'] },
      { rn: 'vi', dur: 1.5, notes: ['A2', 'A4', 'C5', 'E5'] },
      { rn: 'ii', dur: 1.5, notes: ['D3', 'A4', 'D5', 'F5'] },
      { rn: 'V', dur: 1.5, notes: ['G2', 'B4', 'D5', 'G5'] },
      { rn: 'I', dur: 3, notes: ['C3', 'C5', 'E5', 'G5'] },
    ],
    heardIn: [],
    sources: [CAPLIN],
    variants: [[-3, -5, 'G major'], [-1, -2, 'B♭ major']],
  },
  {
    id: 'lament-bass',
    name: 'Lament bass',
    feeling: 'A slow sigh',
    family: 'sequence',
    listenFor: 'In minor, the bass walks down four steps from the tonic to the dominant, as if sighing.',
    plain: 'In a minor key, the bass walks slowly down four steps, as if sighing.',
    theory:
      'A descending tetrachord from 1 to 5 in minor, often used as a repeated ground. Above it the upper voices answer in sighing steps, and the dominant at the bottom asks to begin again.',
    bassFormula: '1 – 7 – 6 – 5',
    harmonyFormula: 'i – v⁶ – iv⁶ – V',
    history: 'An emblem of grief in seventeenth- and eighteenth-century music, from operatic laments to Bach’s Crucifixus.',
    key: 'a',
    prototype: [
      { rn: 'i', dur: 2, notes: ['A2', 'E4', 'C5'] },
      { rn: 'v6', dur: 2, notes: ['G2', 'E4', 'B4'] },
      { rn: 'iv6', dur: 2, notes: ['F2', 'F4', 'A4'] },
      { rn: 'V', dur: 2, notes: ['E2', 'G#4', 'B4'] },
      { rn: 'i', dur: 4, notes: ['A2', 'A4', 'C5'] },
    ],
    heardIn: [],
    sources: [],
    variants: [[3, 5, 'D minor'], [-3, -5, 'E minor']],
  },
  {
    id: 'rule-of-the-octave',
    name: 'Rule of the octave',
    feeling: 'Every bass note has its chord',
    family: 'grammar',
    listenFor: 'The bass climbs a scale and comes back down; above it the chords change so that 1 and 5 feel stable and every other step leans.',
    plain: 'The bass climbs a scale and comes back down; above it the chords change so that home and its strongest neighbour feel settled and every other step leans.',
    theory:
      'A figured-bass model assigning a standard chord to each degree of a scale in the bass: 5/3 on 1 and 5, 6/3 on 3 and 6, and restless 6/5 or 6/4/3 chords on 2, 4 and 7. Learning it teaches the harmonic sense of every scale step.',
    bassFormula: '1 2 3 4 5 6 7 8 · 8 7 6 5 4 3 2 1',
    harmonyFormula: 'I V⁴₃ I⁶ ii⁶₅ V IV⁶ V⁶₅ I · I V⁶ V⁴₃/V V V⁴₂ I⁶ V⁴₃ I',
    history: 'Published by François Campion in 1716 and central to Neapolitan partimento teaching. Fenaroli’s rules, which include it, stayed in print from 1775 to 1930.',
    key: 'C',
    // Realized at run time by the voice-leading engine (see src/data/rule.ts).
    prototype: [],
    whatIf: {
      label: 'Plain triads',
      question: 'What if every bass note carried a root-position triad?',
      effect: 'The scale loses its sense of direction: 3 and 7 now sound like arrivals, and the diminished triad on 7 sounds raw.',
      prototype: [],
    },
    heardIn: [],
    sources: [FENAROLI, SANGUINETTI],
  },
];

export const pathById = (id: string): PathDef | undefined => PATHS.find((p) => p.id === id);
